import React, { useRef, useEffect, useState } from "react";
import { Paper, Button, Typography } from "@material-ui/core";
import { useFormik } from "formik";
import dayjs from "dayjs";
import groupBy from "lodash/groupBy";
import mapValues from "lodash/mapValues";
import useStyles from "@/styles/alarmStyle";
import SearchInput from "@/components/SearchInput";
import { AlarmDeviceListDatatable } from "@/helpers/alarmDeviceListDatatable";
import { useTranslation } from "@/langs/useTranslation";
import { toast } from "@/apis/request";
import { FormItem } from "@/components/Form";
import AlarmRuleList from "@/components/FormFields/AlarmRuleList";
import TagList from "@/components/FormFields/TagList";
import DateRangePicker from "@/components/DateRangePicker";
import {
  resetAlarmDeviceApi,
  unbindDevicesTemplatesApi
} from "@/apis/apiCollection";
import modalConfirm from "@/components/Modal/modalConfirm";
import SubmitButton from "@/components/commons/SubmitButton";
import PublishStatus from "@/components/FormFields/PublishStatus";
import TriggerStatus from "@/components/FormFields/TriggerStatus";
import DeployMembers from "@/components/FormFields/DeployMembers";
import AlarmTemplateList from "@/components/FormFields/AlarmTemplateList";
import { IconFile } from "@/components/Icon";
import { useGlobalState } from "@/GlobalProvider";
import HasPermission from "@/components/commons/HasPermission";
import DeviceGroups from "@/components/FormFields/DeviceGroups/DeviceGroups";
import CollapsePanel, {
  CollapseItem,
  CollapseTrigger
} from "@/components/CollapsePanel";

const AlarmDeviceList = ({ alarmPolicyOptionId, triggerStatus }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const tableRef = useRef(null);
  const selectedRowsRef = useRef([]);
  const isReset = useRef(true);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const updateType = useRef(null);

  const initialValues = {
    template: undefined,
    alarmPolicyOptionId,
    publishStatus: undefined,
    triggerStatus: triggerStatus ? +triggerStatus : undefined,
    group: undefined,
    tag: undefined,
    createUserId: undefined,
    date: [null, null],
    date2: [null, null],
    keywords: ""
  };

  const { currentCompanyId } = useGlobalState();

  const { values, handleChange, handleSubmit /* , handleReset */, resetForm } =
    useFormik({
      initialValues,
      onSubmit: () => {
        handleReloadDataTable();
      }
    });

  const getSearchParams = () => {
    const formValues = isReset.current ? initialValues : values;
    const date = formValues.date;
    const date2 = formValues.date2;
    return {
      alarmTemplateId: formValues.template,
      alarmPolicyOptionId: formValues.alarmPolicyOptionId,
      publishStatus: formValues.publishStatus || "",
      triggerStatus: formValues.triggerStatus || 0,
      groupIds: formValues.group,
      tagIds: formValues.tag,
      createUserId: formValues.createUserId,
      publishDateStart: date[0] ? dayjs(date[0]).valueOf() : undefined,
      publishDateEnd: date[1] ? dayjs(date[1]).valueOf() : undefined,
      triggerDateStart: date2[0] ? dayjs(date2[0]).valueOf() : undefined,
      triggerDateEnd: date2[1] ? dayjs(date2[1]).valueOf() : undefined,
      keywords: formValues.keywords
    };
  };

  const reloadTableWithGroupId = () => {
    isReset.current = false;
    tableRef.current?.reload();
  };

  const resetTableWithGroupId = () => {
    isReset.current = true;
    tableRef.current?.reload();
  };

  const handleReloadDataTable = reset => {
    if (reset) {
      resetTableWithGroupId();
    } else {
      reloadTableWithGroupId();
    }
  };

  const resetQuery = () => {
    initialValues.alarmPolicyOptionId = undefined;
    initialValues.triggerStatus = undefined;
    resetForm({
      values: initialValues
    });
    handleReloadDataTable(true);
  };

  const getSelectedRows = () => {
    const selectedAlarmIds = tableRef.current
      .getSelected()
      .map(id => id.split(","));

    const data = tableRef.current?.api().data().toArray();
    const selectedRows = selectedAlarmIds.map(id =>
      data.find(
        item =>
          item.deviceId === id[0] &&
          item.alarmTemplateId === id[1] &&
          item.alarmPolicyId === id[2]
      )
    );

    if (selectedRows && selectedRows.length) {
      selectedRowsRef.current = selectedRows;
      return selectedRows;
    }
    toast.info(t({ defaultMessage: "未选择任何设备" }));
  };

  const handleLoaded = json => {
    setTotal(json.recordsTotal);
  };

  const doReset = async rows => {
    try {
      updateType.current = "reset";
      setLoading(true);
      if (!Array.isArray(rows)) {
        rows = [rows];
      }
      await resetAlarmDeviceApi({
        resetDevices: rows.map(row => ({
          deviceId: row.deviceId,
          alarmPolicyId: row.alarmPolicyId
        }))
      });
      handleReloadDataTable();
      toast.success(t({ defaultMessage: "操作成功" }));
    } finally {
      setLoading(false);
    }
  };

  const doDelete = async (rows, isBatch) => {
    try {
      updateType.current = "delete";
      setLoading(true);
      if (!Array.isArray(rows)) {
        rows = [rows];
      }
      if (isBatch) {
        // key: templateId, value: deviceIds
        const params = mapValues(
          groupBy(
            rows.map(row => ({
              alarmTemplateId: row.alarmTemplateId,
              deviceId: row.deviceId
            })),
            row => row.alarmTemplateId
          ),
          subRow => [...new Set(subRow.map(item => item.deviceId))]
        );
        await unbindDevicesTemplatesApi({
          template_device_maps: params
        });
      } else {
        await unbindDevicesTemplatesApi({
          device_ids: [...new Set(rows.map(row => row.deviceId))],
          alarm_template_ids: [...new Set(rows.map(row => row.alarmTemplateId))]
        });
      }
      handleReloadDataTable();
      toast.success(t({ defaultMessage: "删除成功" }));
    } finally {
      setLoading(false);
    }
  };

  const handleAlarmReset = row => {
    if (row.alarmPolicyOptionName === "App Crash Detection") {
      modalConfirm({
        title: t({ defaultMessage: "重启提醒" }),
        content: t(
          {
            defaultMessage:
              "是否确认重启（{name}）的{appName}？重启后将继续执行应用监控。"
          },
          { name: row.deviceSerialNumber, appName: JSON.parse(row.alarmPolicyExt || "{}").app }
        ),
        onConfirm: () => {
          return doReset(row);
        }
      });
    } else {
      modalConfirm({
        title: t({ defaultMessage: "重置提醒" }),
        content: t(
          {
            defaultMessage:
              "是否确认重置（{name}）该报警设备？重置后报警规则将继续执行监控。"
          },
          { name: row.deviceSerialNumber }
        ),
        onConfirm: () => {
          return doReset(row);
        }
      });
    }
  };

  const handleAlarmDelete = row => {
    modalConfirm({
      title: t({ defaultMessage: "删除提醒" }),
      content: t(
        {
          defaultMessage: "是否确认删除（{name}）当前设备？"
        },
        { name: row.deviceSerialNumber }
      ),
      onConfirm: () => {
        return doDelete(row);
      }
    });
  };

  const handleBatchAlarmReset = () => {
    if (getSelectedRows()) {
      modalConfirm({
        title: t({ defaultMessage: "重置提醒" }),
        content: t(
          {
            defaultMessage:
              "是否确认重置（{name}）该报警设备？重置后报警规则将继续执行监控。"
          },
          {
            name: [
              ...new Set(
                selectedRowsRef.current.map(item => item.deviceSerialNumber)
              )
            ].join("、")
          }
        ),
        onConfirm: () => {
          return doReset(selectedRowsRef.current);
        }
      });
    }
  };

  const handleBatchAlarmDelete = () => {
    if (getSelectedRows()) {
      modalConfirm({
        title: t({ defaultMessage: "删除提醒" }),
        content: t(
          {
            defaultMessage: "是否确认删除（{name}）该报警设备？"
          },
          {
            name: [
              ...new Set(
                selectedRowsRef.current.map(item => item.deviceSerialNumber)
              )
            ].join("、")
          }
        ),
        onConfirm: () => {
          return doDelete(selectedRowsRef.current, true);
        }
      });
    }
  };

  useEffect(() => {
    handleReloadDataTable(true);
  }, [currentCompanyId]); // eslint-disable-line

  return (
    <div className={classes.main}>
      <Paper elevation={0}>
        <form onSubmit={handleSubmit}>
          <div className={classes.tableHeader}>
            <div className={classes.tableTop}>
              <div>
                <Typography variant="h5" component="span">
                  {t({ defaultMessage: "报警设备列表" })}
                </Typography>
                <Typography
                  variant="body2"
                  color="primary"
                  component="span"
                  className={classes.subTitle}
                >
                  <IconFile size={15} />
                  {t({ defaultMessage: "{num}条" }, { num: total })}
                </Typography>
              </div>
              <div className={classes.rightAction}>
                <SearchInput
                  placeholder={t({ defaultMessage: "搜索" })}
                  name="keywords"
                  icon={null}
                  value={values.keywords}
                  onChange={handleChange}
                />
              </div>
            </div>
            <CollapsePanel>
              <div className={classes.tableQueryForm}>
                <FormItem
                  label={t({ defaultMessage: "报警模板" })}
                  className={classes.formItem}
                >
                  <AlarmTemplateList
                    name="template"
                    value={values.template}
                    onChange={handleChange}
                  />
                </FormItem>
                <FormItem
                  label={t({ defaultMessage: "报警规则" })}
                  className={classes.formItem}
                >
                  <AlarmRuleList
                    name="alarmPolicyOptionId"
                    value={values.alarmPolicyOptionId}
                    onChange={handleChange}
                  />
                </FormItem>
                <FormItem
                  label={t({ defaultMessage: "发布状态" })}
                  className={classes.formItem}
                >
                  <PublishStatus
                    name="publishStatus"
                    value={values.publishStatus}
                    onChange={handleChange}
                  />
                </FormItem>
                <FormItem
                  label={t({ defaultMessage: "触发状态" })}
                  className={classes.formItem}
                >
                  <TriggerStatus
                    name="triggerStatus"
                    value={values.triggerStatus}
                    onChange={handleChange}
                  />
                </FormItem>
                <CollapseItem>
                  <FormItem
                    label={t({ defaultMessage: "门店" })}
                    className={classes.formItem}
                  >
                    <DeviceGroups
                      name="group"
                      value={values.group}
                      onChange={handleChange}
                    />
                  </FormItem>
                  <FormItem
                    label={t({ defaultMessage: "标签" })}
                    className={classes.formItem}
                  >
                    <TagList
                      name="tag"
                      value={values.tag}
                      onChange={handleChange}
                    />
                  </FormItem>
                  <FormItem
                    label={t({ defaultMessage: "部署人" })}
                    className={classes.formItem}
                  >
                    <DeployMembers
                      name="createUserId"
                      value={values.createUserId}
                      onChange={handleChange}
                    />
                  </FormItem>
                  <FormItem
                    label={t({ defaultMessage: "报警发布时间" })}
                    className={classes.formItem}
                  >
                    <DateRangePicker
                      name="date"
                      value={values.date}
                      onChange={handleChange}
                    />
                  </FormItem>
                  <FormItem
                    label={t({ defaultMessage: "最后触发时间" })}
                    className={classes.formItem}
                  >
                    <DateRangePicker
                      name="date2"
                      value={values.date2}
                      onChange={handleChange}
                    />
                  </FormItem>
                </CollapseItem>
                <FormItem className={classes.formItem}>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    style={{ marginRight: 8 }}
                  >
                    {t({ defaultMessage: "查询" })}
                  </Button>
                  <Button variant="outlined" onClick={resetQuery}>
                    {t({ defaultMessage: "重置" })}
                  </Button>
                  <CollapseTrigger />
                </FormItem>
              </div>
            </CollapsePanel>
          </div>
        </form>
        <AlarmDeviceListDatatable
          ref={tableRef}
          getSearchParams={getSearchParams}
          onReset={handleAlarmReset}
          onDelete={handleAlarmDelete}
          onLoaded={handleLoaded}
          footer={
            <>
              <HasPermission permission="alarm_list_alarm_reset">
                <SubmitButton
                  variant="outlined"
                  color="primary"
                  loading={loading && updateType.current === "reset"}
                  onClick={handleBatchAlarmReset}
                >
                  {t({ defaultMessage: "批量重置" })}
                </SubmitButton>
              </HasPermission>
              {/* <HasPermission permission="alarm_list_alarm_delete"> */}
              <SubmitButton
                variant="outlined"
                color="primary"
                loading={loading && updateType.current === "delete"}
                onClick={handleBatchAlarmDelete}
              >
                {t({ defaultMessage: "批量删除" })}
              </SubmitButton>
              {/* </HasPermission> */}
            </>
          }
        />
      </Paper>
    </div>
  );
};

export default AlarmDeviceList;
